import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { injectIntl, intlShape } from '../../util/reactIntl';
import { useLocation, withRouter } from 'react-router-dom/cjs/react-router-dom.min';
import { useConfiguration } from '../../context/configurationContext';
import { IconSearch, IconSpinner, LayoutSingleColumn, Page } from '../../components';
import TopbarContainer from '../TopbarContainer/TopbarContainer';
import facebookImage from '../../assets/biketribe-facebook-sharing-1200x630.jpg';
import twitterImage from '../../assets/biketribe-twitter-sharing-600x314.jpg';
import { connect, useDispatch, useSelector } from 'react-redux';
import { compose } from 'redux';
import { withViewport } from '../../util/uiHelpers';
import { isScrollingDisabled } from '../../ducks/ui.duck';
import BlogCard from './BlogCard/BlogCard';
import { fetchAllBlogs, getBlogsInProgress, getBlogsSelector } from '../../ducks/strapi.duck';
import { isArrayLength } from '../../util/genericHelpers';
import { parse } from '../../util/urlHelpers';

import css from './BlogPage.module.css';

export const BlogPageComponent = props => {
  const { intl, scrollingDisabled } = props;

  const config = useConfiguration();
  const dispatch = useDispatch();
  const location = useLocation();

  const search = parse(location.search);

  useEffect(() => {
    dispatch(fetchAllBlogs({ tag: search?.tag }));
  }, [search?.tag]);

  const blogsData = useSelector(getBlogsSelector) || [];

  const searchInProgress = useSelector(getBlogsInProgress);

  const [searchKeyword, setSearchKeyword] = useState('');
  const [filteredBlogs, setFilteredBlogs] = useState([]);
  const [sortBy, setSortBy] = useState('recent'); // Default sorting by most recent

  // Effect to filter and sort blogs based on search keyword and sorting option
  useEffect(() => {
    let processedBlogs = [...blogsData];

    // Filter by search keyword
    if (searchKeyword) {
      processedBlogs = processedBlogs.filter(blog => {
        const title = blog.title.toLowerCase();
        const keyword = searchKeyword.toLowerCase();
        return title.includes(keyword);
      });
    }

    // Sort blogs
    if (sortBy === 'recent') {
      processedBlogs.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    } else if (sortBy === 'relevance') {
      // Example: Sort by a hypothetical 'relevanceScore' property
      processedBlogs.sort((a, b) => b.relevanceScore - a.relevanceScore);
    }

    setFilteredBlogs(processedBlogs);
  }, [searchKeyword, sortBy, blogsData]); // Dependencies

  const blogs = isArrayLength(filteredBlogs) ? filteredBlogs : blogsData;

  // Handler for search input changes
  const handleSearchChange = event => {
    setSearchKeyword(event.target.value);
  };

  const handleSortChange = event => {
    setSortBy(event.target.value);
  };

  const schemaImage = `${config.marketplaceRootURL}${facebookImage}`;
  const siteTitle = config.marketplaceName;

  const schemaTitle = intl.formatMessage({ id: 'BlogPage.schemaTitle' }, { siteTitle });
  const schemaDescription = intl.formatMessage({
    id: 'BlogPage.schemaDescription',
  });

  return (
    <Page
      className={css.root}
      scrollingDisabled={scrollingDisabled}
      contentType="website"
      description={schemaDescription}
      title={siteTitle}
      facebookImages={[{ url: facebookImage, width: 1200, height: 630 }]}
      twitterImages={[
        {
          url: `${config.marketplaceRootURL}${twitterImage}`,
          width: 600,
          height: 314,
        },
      ]}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'WebPage',
        description: schemaDescription,
        name: schemaTitle,
        image: [schemaImage],
      }}
    >
      <LayoutSingleColumn className={css.pageRoot} topbar={<TopbarContainer />}>
        <div className={css.pageContent}>
          <div className={css.blogsContainer}>
            <div className={css.blogsContent}>
              <div className={css.blogHeader}>
                <span>{intl.formatMessage({ id: 'BlogPage.ourBlog' })}</span>
                <h2>{intl.formatMessage({ id: 'BlogPage.latestWritingsHeading' })}</h2>
                <div className={css.searchForm}>
                  <IconSearch />
                  <input
                    type="text"
                    placeholder={intl.formatMessage({ id: 'BlogPage.searchPlaceholder' })}
                    value={searchKeyword}
                    onChange={handleSearchChange}
                  />
                </div>
              </div>
              <div className={css.mainBlogWrapper}>
                <div className={css.blogFilterSec}>
                  <div className={css.blogFilterSorting}>
                    <select name="sortby" id="sortby" value={sortBy} onChange={handleSortChange}>
                      <option value="recent">
                        {intl.formatMessage({ id: 'BlogPage.sortByMostRecent' })}
                      </option>
                      <option value="relevance">
                        {intl.formatMessage({ id: 'BlogPage.sortByRelevance' })}
                      </option>
                    </select>
                  </div>
                </div>
                {searchInProgress ? (
                  <div className={css.spinner}>
                    <IconSpinner />
                  </div>
                ) : (
                  <div className={css.mainBlogContent}>
                    {isArrayLength(blogs) ? (
                      blogs.map((blog, index) => <BlogCard key={index} blog={blog} />)
                    ) : (
                      <p>{intl.formatMessage({ id: 'BlogPage.noBlogPostsFound' })}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

const { bool } = PropTypes;

BlogPageComponent.propTypes = {
  scrollingDisabled: bool.isRequired,
  // from injectIntl
  intl: intlShape.isRequired,
};

const mapStateToProps = state => {
  return {
    scrollingDisabled: isScrollingDisabled(state),
  };
};

const mapDispatchToProps = dispatch => ({
  onUpdateProfile: data => dispatch(updateProfile(data)),
});

const BlogPage = compose(
  withViewport,
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  injectIntl
)(BlogPageComponent);

export default BlogPage;
